import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import countryCodes from "../../CountryCode/CountryCode.json";
import AuthUser from "../../Hooks/authUser";

const AdminDashboardIndex = () => {
  const { userInfo } = AuthUser();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [countryCode, setCountryCode] = useState(
    countryCodes[0]?.dial_code || ""
  );

  const fetchDoctors = async () => {
    try {
      const response = await fetch(
        `https://servercareseebd.careseebd.com/api/v1/user/specific?fieldName=role&&fieldValue=doctor&&fieldName2=referralId&&fieldValue2=${userInfo?._id}`,
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
          },
        }
      );
      const data = await response.json();
      setDoctors(data?.data || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  useEffect(() => {
    if (userInfo?._id) {
      fetchDoctors();
    }
  }, [userInfo?._id]);

  const createUser = async (user) => {
    try {
      const response = await fetch("https://servercareseebd.careseebd.com/api/v1/user/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new Error(responseData.message || "Failed to create Doctor.");
      }

      toast.success("Doctor created successfully!");
      fetchDoctors(); // Refresh the list counts
    } catch (error) {
      toast.error(error.message || "An error occurred.");
    }
  };

  const handelSubmit = async (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const email = e.target.email.value;
    const phoneWithCountryCode = `${countryCode}${phone}`;
    if (!password) {
      toast.error("Please provide or generate a password");
      return;
    }

    const user = {
      role: "doctor",
      name,
      email,
      password,
      phone: phoneWithCountryCode,
      confirmPassword: password,
      referralId: userInfo._id,
      referralName: userInfo.name,
      referralEmail: userInfo.email,
      referralPhone: userInfo.phone,
    };

    try {
      await createUser(user);
      setPassword("");
      setPhone("");
      e.target.reset(); // Reset the form
    } catch (error) {
      console.error("Error creating user:", error);
    }
  };

  const generatePassword = (event) => {
    event.preventDefault();
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generatedPassword = "";
    for (let i = 0; i < 8; i++) {
      generatedPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    setPassword(generatedPassword);
  };

  const activeDoctorsCount = doctors.filter((d) => d.isActive).length;
  const inactiveDoctorsCount = doctors.filter((d) => !d.isActive).length;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Overview Stat Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Doctors */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Registered Doctors</span>
            <p className="text-2xl font-black text-slate-800 tracking-tight">{doctors.length}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 text-secondary flex items-center justify-center">
            <Icon icon="solar:users-group-two-rounded-bold-duotone" className="text-2xl" />
          </div>
        </div>

        {/* Active Doctors */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Active Clinics</span>
            <p className="text-2xl font-black text-green-600 tracking-tight">{activeDoctorsCount}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
            <Icon icon="solar:shield-check-bold-duotone" className="text-2xl" />
          </div>
        </div>

        {/* Inactive Doctors */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Pending Accounts</span>
            <p className="text-2xl font-black text-amber-600 tracking-tight">{inactiveDoctorsCount}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <Icon icon="solar:shield-warning-bold-duotone" className="text-2xl" />
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column: Form Card */}
        <div className="lg:col-span-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
          <div>
            <h2 className="text-base font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
              <Icon icon="solar:user-plus-bold-duotone" className="text-secondary text-xl" />
              <span>Add New Doctor</span>
            </h2>
            <p className="text-xs text-slate-400 font-semibold mt-0.5">Create a doctor login profile with default therapy access credentials.</p>
          </div>

          <form onSubmit={handelSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name input */}
              <div className="relative">
                <Icon
                  icon="solar:user-linear"
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg"
                />
                <input
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700 bg-white"
                  type="text"
                  name="name"
                  required
                  placeholder="Doctor's Full Name"
                />
              </div>

              {/* Email input */}
              <div className="relative">
                <Icon
                  icon="solar:letter-linear"
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg"
                />
                <input
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700 bg-white"
                  type="email"
                  name="email"
                  required
                  placeholder="Official Email Address"
                />
              </div>

              {/* Phone Input with Country Code Dropdown */}
              <div className="flex items-stretch border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-secondary transition bg-white">
                <div className="flex items-center pl-3.5 text-slate-400">
                  <Icon icon="solar:phone-linear" className="text-lg" />
                </div>
                <select
                  className="pl-2 pr-1 py-2.5 bg-white focus:outline-none text-xs font-bold text-slate-600 border-r border-slate-150 max-w-[100px] truncate"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.dial_code}>
                      {country.code} ({country.dial_code})
                    </option>
                  ))}
                </select>
                <input
                  className="flex-1 px-3 py-2.5 focus:outline-none text-sm font-medium text-slate-700"
                  type="number"
                  name="phone"
                  required
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              {/* Password Generator Input */}
              <div className="relative flex items-stretch border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-secondary transition bg-white">
                <div className="flex items-center pl-3.5 text-slate-400">
                  <Icon icon="solar:lock-password-linear" className="text-lg" />
                </div>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter or generate password"
                  className="flex-1 min-w-0 px-3 py-2.5 focus:outline-none text-sm font-medium text-slate-700"
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border-l border-slate-200 text-slate-650 font-bold text-xs transition duration-150 shrink-0"
                >
                  Generate
                </button>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                className="bg-secondary hover:bg-secondary/95 text-white font-bold py-2.5 px-6 rounded-xl shadow-md shadow-indigo-900/10 hover:shadow-lg transition duration-200 hover:-translate-y-0.5 active:translate-y-0 text-sm flex items-center gap-1.5 cursor-pointer"
              >
                <Icon icon="solar:user-plus-bold" className="text-base" />
                <span>Register Doctor</span>
              </button>
            </div>
          </form>
        </div>

        {/* Right Column: Info / Guidelines Card */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-wider">Administrative Guidelines</h3>
            <p className="text-xs text-slate-500 leading-relaxed font-semibold">
              Doctors registered through this panel are assigned directly under your system administration.
            </p>
            <div className="space-y-2.5 pt-2">
              <div className="flex items-start gap-2 text-xs text-slate-600 font-medium">
                <Icon icon="solar:shield-keyhole-linear" className="text-secondary text-base shrink-0 mt-0.5" />
                <span>New accounts default to Inactive until verified or configured.</span>
              </div>
              <div className="flex items-start gap-2 text-xs text-slate-600 font-medium">
                <Icon icon="solar:bill-list-linear" className="text-secondary text-base shrink-0 mt-0.5" />
                <span>You can edit specific physical packages costs and subscription configurations in the Doctor Details Modal.</span>
              </div>
            </div>
          </div>
          <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50 text-[11px] font-semibold text-slate-500 flex items-center gap-2.5">
            <Icon icon="solar:info-square-bold-duotone" className="text-secondary text-lg shrink-0" />
            <span>Click on "Doctors List" to view profiles, manage license packages, and check clinic patients.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardIndex;
